import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { BookOpen, CheckCircle } from 'lucide-react';

function ConviteEmprestimo() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [emprestimo, setEmprestimo] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [sessao, setSessao] = useState(null);
  const [confirmando, setConfirmando] = useState(false);
  const [confirmado, setConfirmado] = useState(false);
  const [erro, setErro] = useState(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSessao(data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((_, s) => setSessao(s));
    return () => sub.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    async function carregar() {
      const { data, error } = await supabase
        .from('emprestimos')
        .select('id, status, leitor_nome, leitor_user_id, prazo_dias, data_devolucao, livros(titulo, autor, capa_url)')
        .eq('id', id)
        .maybeSingle();
      if (error || !data) { setErro('Convite não encontrado ou já expirou.'); }
      else setEmprestimo(data);
      setCarregando(false);
    }
    if (id) carregar();
  }, [id]);

  async function handleAceitar() {
    if (!sessao) {
      localStorage.setItem('redirect_after_login', `/convite/emprestimo/${id}`);
      navigate('/login');
      return;
    }
    setConfirmando(true);
    const userId = sessao.user.id;
    const { data: perfil } = await supabase.from('perfis').select('nome').eq('id', userId).maybeSingle();
    await supabase.from('emprestimos').update({
      leitor_user_id: userId,
      leitor_nome: perfil?.nome || sessao.user.email,
    }).eq('id', id).is('leitor_user_id', null);
    setConfirmado(true);
    setConfirmando(false);
  }

  if (carregando) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center">
        <p className="text-sm text-ink-400">Carregando...</p>
      </div>
    );
  }

  if (erro || !emprestimo) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center px-6">
        <div className="text-center">
          <p className="text-[15px] font-semibold text-ink-900 mb-2">Convite não encontrado</p>
          <p className="text-[13px] text-ink-400 mb-5">{erro || 'Este link pode ter expirado ou ser inválido.'}</p>
          <button onClick={() => navigate('/')}
            className="px-6 py-2.5 rounded-[12px] text-[13px] font-semibold text-white border-0 cursor-pointer"
            style={{background:'#5B3CE4'}}>
            Ir para o início
          </button>
        </div>
      </div>
    );
  }

  const livro = emprestimo.livros;
  const jaVinculado = !!emprestimo.leitor_user_id;

  return (
    <div className="min-h-screen bg-paper flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-line-soft">
          {confirmado ? (
            <div className="text-center py-4">
              <CheckCircle size={44} color="#1FA971" strokeWidth={1.5} className="mb-3 mx-auto" />
              <p className="text-[16px] font-bold text-ink-900 mb-1">Empréstimo confirmado!</p>
              <p className="text-[13px] text-ink-400 mb-5">O livro aparece agora no seu Acervo.</p>
              <button onClick={() => navigate('/home')}
                className="w-full py-3 rounded-[14px] text-[14px] font-extrabold text-white border-0 cursor-pointer"
                style={{background:'#5B3CE4'}}>
                Ver no meu Acervo
              </button>
            </div>
          ) : (
            <>
              <p className="text-[11px] font-bold uppercase tracking-wider text-ink-400 mb-4">Convite de empréstimo</p>
              <div className="flex gap-3 mb-5">
                <div className="w-12 h-16 rounded-[8px] overflow-hidden bg-paper-2 flex items-center justify-center flex-shrink-0">
                  {livro?.capa_url
                    ? <img src={livro.capa_url} alt="" className="w-full h-full object-cover" />
                    : <BookOpen size={20} color="#C4BCDA" strokeWidth={1} />}
                </div>
                <div>
                  <p className="text-[15px] font-bold text-ink-900 leading-tight mb-0.5">{livro?.titulo}</p>
                  <p className="text-[12px] text-ink-400">{livro?.autor}</p>
                </div>
              </div>
              <p className="text-[13px] text-ink-600 mb-5">
                Alguem registrou que te emprestou este livro. Confirme para aparecer no seu Acervo.
              </p>
              {emprestimo.data_devolucao && (
                <div className="bg-paper rounded-[10px] px-4 py-2.5 mb-5 flex items-center justify-between">
                  <span className="text-[12px] text-ink-400">Devolução prevista</span>
                  <span className="text-[12px] font-semibold text-ink-700">
                    {new Date(emprestimo.data_devolucao + 'T12:00:00').toLocaleDateString('pt-BR')}
                  </span>
                </div>
              )}
              {jaVinculado ? (
                <p className="text-[13px] text-ink-400 text-center py-2">Este empréstimo já foi confirmado.</p>
              ) : (
                <div className="flex flex-col gap-2">
                  <button onClick={handleAceitar} disabled={confirmando}
                    className="w-full py-3 rounded-[14px] text-[14px] font-extrabold text-white border-0 cursor-pointer disabled:opacity-50"
                    style={{background:'#5B3CE4'}}>
                    {confirmando ? 'Confirmando...' : sessao ? 'Confirmar que recebi' : 'Entrar para confirmar'}
                  </button>
                  {!sessao && (
                    <button onClick={() => { localStorage.setItem('redirect_after_login', `/convite/emprestimo/${id}`); navigate('/cadastro'); }}
                      className="w-full py-2.5 rounded-[14px] text-[13px] font-semibold border border-line text-ink-500 bg-transparent cursor-pointer">
                      Criar conta
                    </button>
                  )}
                  <button onClick={() => navigate('/')}
                    className="w-full py-2 text-[12px] text-ink-400 border-0 bg-transparent cursor-pointer">
                    Ignorar por agora
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ConviteEmprestimo;
